import { isNavTransitioning, onNavTransitionEnd } from "./navTransition";
import { useEffect, useMemo, useRef, useState } from "react";

export interface ElementSize {
  width: number;
  height: number;
}

/**
 * Tracks an element's content-box size via ResizeObserver. Returns 0x0
 * until the first observation fires.
 *
 * The returned ref is a getter/setter ref object rather than a plain `useRef`
 * so a *late* mount still gets observed: callers that render a skeleton first
 * (ChartWidget) attach the ref only after loading finishes, and a mount-once
 * effect reading `ref.current` would have captured `null` forever.
 */
export function useElementSize<T extends HTMLElement = HTMLDivElement>() {
  const nodeRef = useRef<T | null>(null);
  const [node, setNode] = useState<T | null>(null);
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  const ref = useMemo(
    () =>
      ({
        get current() {
          return nodeRef.current;
        },
        set current(next: T | null) {
          if (nodeRef.current === next) return;
          nodeRef.current = next;
          setNode(next);
        },
      }) as React.MutableRefObject<T | null>,
    [],
  );

  useEffect(() => {
    if (!node) return;
    // While the nav shelf resizes, keep only the latest size and apply it once when it ends.
    let held: ElementSize | null = null;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (isNavTransitioning()) {
        held = { width, height };
        return;
      }
      setSize({ width, height });
    });
    observer.observe(node);
    const stopWaiting = onNavTransitionEnd(() => {
      if (held) setSize(held);
      held = null;
    });

    // A window resize (dragging the browser edge, rotating a device, toggling
    // DevTools) can deliver a ResizeObserver entry mid-reflow, while flex/grid
    // ancestors are still settling into their final size. Consumers that feed
    // that contentRect straight into a third-party renderer (Vega) can render
    // at that in-between size and never get nudged again, because the box
    // then stops changing size and no further entry ever arrives. Re-observing
    // after two animation frames forces one more notification with the
    // settled contentRect, making it authoritative — the same
    // remeasureAfterViewportSettles technique useWidgetHeightReport already
    // uses for this exact class of stale measurement.
    let settleFrame: number | null = null;
    const reobserveAfterViewportSettles = () => {
      if (settleFrame !== null) cancelAnimationFrame(settleFrame);
      settleFrame = requestAnimationFrame(() => {
        settleFrame = requestAnimationFrame(() => {
          settleFrame = null;
          observer.unobserve(node);
          observer.observe(node);
        });
      });
    };
    window.addEventListener("resize", reobserveAfterViewportSettles);

    return () => {
      window.removeEventListener("resize", reobserveAfterViewportSettles);
      if (settleFrame !== null) cancelAnimationFrame(settleFrame);
      stopWaiting();
      observer.disconnect();
    };
  }, [node]);

  return [ref, size] as const;
}
